import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('bizmonitor_token'));
  const [loading, setLoading] = useState(true);
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState(null);
  const [pendingEmail, setPendingEmail] = useState(null);

  const logout = useCallback(() => {
    localStorage.removeItem('bizmonitor_token');
    setToken(null);
    setUser(null);
    setRequires2FA(false);
    setTempToken(null);
    setPendingEmail(null);
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API_URL}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          logout();
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token, logout]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    
    if (response.data.requires_2fa) {
      setRequires2FA(true);
      setTempToken(response.data.temp_token);
      setPendingEmail(email);
      return { requires2FA: true };
    }
    
    localStorage.setItem('bizmonitor_token', response.data.token);
    setToken(response.data.token);
    setUser(response.data.user);
    return { success: true };
  };

  const verify2FA = async (code) => {
    const response = await axios.post(`${API_URL}/auth/verify-2fa`, {
      email: pendingEmail,
      code,
      temp_token: tempToken
    });
    
    localStorage.setItem('bizmonitor_token', response.data.token);
    setToken(response.data.token);
    setUser(response.data.user);
    setRequires2FA(false);
    setTempToken(null);
    setPendingEmail(null);
    return { success: true };
  };

  const register = async (email, password, fullName) => {
    await axios.post(`${API_URL}/auth/register`, {
      email,
      password,
      full_name: fullName
    });
    return { success: true };
  };

  const setup2FA = async () => {
    const response = await axios.post(`${API_URL}/auth/setup-2fa`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  };

  const enable2FA = async (code) => {
    const response = await axios.post(`${API_URL}/auth/enable-2fa?code=${code}`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      requires2FA,
      login,
      logout,
      register,
      verify2FA,
      setup2FA,
      enable2FA,
      isAuthenticated: !!token && !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
